import { MediaType } from '@/types';
import getMediaTypeFromString from '@/utils/generation/getMediaTypeFromString';

import type { FlowMedia } from '@/types';
import type { PollResponse } from '@/requests/magic-flow/pollRequest';

export interface BuiltFlowMedia {
  resultFile: FlowMedia;
  resultType: string;
  isVideo: boolean;
  isAudio: boolean;
}

type PollResultEntry = NonNullable<PollResponse['response']['result']>[number];

/**
 * One result entry as `BuiltFlowMedia`, or `null` when it carries no url.
 *
 * `id` is the generation id for every entry of the same run — the entries are
 * outputs of one job, and each is told apart by its own `driveUid`.
 */
const buildOne = (
  generationId: string,
  entry: PollResultEntry | undefined,
  fallbackUrl?: string
): BuiltFlowMedia | null => {
  const resultUrl = entry?.result?.url || fallbackUrl;
  if (!resultUrl) return null;

  const resultType = entry?.type?.toLowerCase() ?? '';
  const mediaType = getMediaTypeFromString(resultType);
  const isVideo = mediaType === MediaType.VIDEO;
  const isAudio = mediaType === MediaType.AUDIO;

  const resultFile: FlowMedia = {
    id: generationId,
    url: resultUrl,
    width: entry?.result?.metadata?.width,
    height: entry?.result?.metadata?.height,
    type: mediaType,
    driveUid: entry?.driveUid,
    poster: isVideo ? entry?.result?.frames?.first : undefined,
  };

  return { resultFile, resultType, isVideo, isAudio };
};

/**
 * Every result a poll response carries, in the order the backend returned them.
 *
 * A flow whose CMS config sets `outputsCount > 1` answers with several entries
 * from one job; anything else answers with one. Entries without a url are
 * dropped rather than surfaced as broken tiles.
 *
 * Throws when nothing usable came back — the caller treats that as a failed
 * generation.
 */
const buildFlowMediaList = ({
  generationId,
  pollingResponse,
}: {
  generationId: string;
  pollingResponse: PollResponse;
}): BuiltFlowMedia[] => {
  const entries = pollingResponse.response.result ?? [];
  const fallbackUrl = pollingResponse.response.output?.url;

  const built = entries.length
    ? entries
        .map((entry) => buildOne(generationId, entry))
        .filter((item): item is BuiltFlowMedia => item !== null)
    : // No `result` array at all — older flows answer with a bare `output`.
      [buildOne(generationId, undefined, fallbackUrl)].filter(
        (item): item is BuiltFlowMedia => item !== null
      );

  if (!built.length) {
    throw new Error('No result URL returned from generation');
  }

  return built;
};

export default buildFlowMediaList;
